/**
 * CheTuo Web — Order create page (创建工单).
 *
 * Flow:
 * 1. Search existing customer/vehicle by phone or plate
 * 2. Or enter new customer + vehicle info
 * 3. Add repair items (from templates or manual)
 * 4. Submit via quick-create API
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { apiClient, mpPath, shopMobilePath } from '../../api/client';
import { ApiError } from '../../api/client';

interface QuickSearchItem {
  customer: { id: string; name: string; phone: string | null };
  vehicle: {
    id: string;
    plate: string;
    brand: string | null;
    model: string | null;
    vin: string | null;
  } | null;
}

interface QuickSearchResult {
  matched: boolean;
  items: QuickSearchItem[];
}

interface ServiceItem {
  id: string;
  name: string;
  category: string | null;
  defaultPrice: string;
  isQuick: boolean;
}

interface OrderItemInput {
  name: string;
  quantity: string;
  unitPrice: string;
  amount: string;
}

interface QuickCreateResult {
  id: string;
  status: string;
  totalAmount: string;
  customerId: string;
  vehicleId: string;
  isNewCustomer: boolean;
  isNewVehicle: boolean;
}

export function OrderCreatePage(): React.JSX.Element {
  const navigate = useNavigate();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState<QuickSearchResult | null>(null);
  const [selectedItem, setSelectedItem] = useState<QuickSearchItem | null>(null);

  // New customer/vehicle
  const [isNew, setIsNew] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [vehicleBrand, setVehicleBrand] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleVin, setVehicleVin] = useState('');

  // Items
  const [items, setItems] = useState<OrderItemInput[]>([]);
  const [remark, setRemark] = useState('');
  const [error, setError] = useState('');

  // Service item templates
  const { data: serviceItems } = useQuery({
    queryKey: ['service-items'],
    queryFn: () => apiClient.get<ServiceItem[]>(shopMobilePath('/service-items')),
  });

  const searchMutation = useMutation({
    mutationFn: (q: string) =>
      apiClient.get<QuickSearchResult>(
        mpPath(`/vehicles/quick-search?q=${encodeURIComponent(q)}`),
      ),
    onSuccess: (result) => {
      const q = searchQuery.trim();
      setSearchResult(result);
      if (!result.matched || result.items.length === 0) {
        setIsNew(true);
        const isPhone = /^\d{11}$/.test(q);
        setCustomerPhone(isPhone ? q : '');
        setVehiclePlate(isPhone ? '' : q);
      } else if (result.items.length === 1) {
        setSelectedItem(result.items[0]!);
        setIsNew(false);
      }
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '搜索失败');
    },
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        idempotencyKey: `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
        items: items.map((i) => ({ name: i.name, quantity: i.quantity, unitPrice: i.unitPrice })),
        remark: remark || undefined,
      };
      if (selectedItem?.vehicle) {
        payload['existingCustomerId'] = selectedItem.customer.id;
        payload['existingVehicleId'] = selectedItem.vehicle.id;
      } else {
        const plate = vehiclePlate.trim().toUpperCase();
        if (!plate) throw new Error('请输入车牌');
        payload['vehicle'] = {
          plate,
          vin: vehicleVin || undefined,
          brand: vehicleBrand || undefined,
          model: vehicleModel || undefined,
          year: vehicleYear ? parseInt(vehicleYear, 10) : undefined,
        };
        if (selectedItem) {
          payload['existingCustomerId'] = selectedItem.customer.id;
        } else if (customerName.trim() || customerPhone.trim()) {
          payload['customer'] = {
            name: customerName.trim() || undefined,
            phone: customerPhone.trim() || undefined,
          };
        }
      }
      return apiClient.post<QuickCreateResult>(mpPath('/orders/quick-create'), payload);
    },
    onSuccess: (result) => {
      navigate(`/shop-admin/orders/${result.id}`);
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '创建工单失败');
    },
  });

  const handleSearch = () => {
    const q = searchQuery.trim();
    if (!q) return;
    setError('');
    setSelectedItem(null);
    setIsNew(false);
    searchMutation.mutate(q);
  };

  const handleAddItem = (item?: ServiceItem) => {
    if (item) {
      setItems([...items, {
        name: item.name,
        quantity: '1',
        unitPrice: item.defaultPrice,
        amount: item.defaultPrice,
      }]);
    } else {
      setItems([...items, { name: '', quantity: '1', unitPrice: '0', amount: '0' }]);
    }
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: keyof OrderItemInput, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index]!, [field]: value };
    if (field === 'quantity' || field === 'unitPrice') {
      const qty = parseFloat(newItems[index]!.quantity) || 0;
      const price = parseFloat(newItems[index]!.unitPrice) || 0;
      newItems[index]!.amount = (qty * price).toFixed(2);
    }
    setItems(newItems);
  };

  const totalAmount = items.reduce((sum, item) => sum + parseFloat(item.amount || '0'), 0).toFixed(2);

  const handleSubmit = () => {
    setError('');
    if (items.length === 0) {
      setError('请添加至少一个维修项目');
      return;
    }
    for (const item of items) {
      if (!item.name.trim()) {
        setError('项目名称不能为空');
        return;
      }
    }
    createMutation.mutate();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/shop-admin/orders')}
          className="text-gray-500 hover:text-gray-700"
        >
          ← 返回列表
        </button>
        <h1 className="text-2xl font-bold text-gray-900">创建工单</h1>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600">{error}</div>
      )}

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>搜索客户/车辆</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="输入手机号或车牌搜索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="max-w-xs"
            />
            <Button
              onClick={handleSearch}
              disabled={searchMutation.isPending}
              variant="outline"
            >
              {searchMutation.isPending ? '搜索中...' : '搜索'}
            </Button>
          </div>

          {/* Search results */}
          {searchResult && searchResult.matched && searchResult.items.length > 1 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm text-gray-600">找到 {searchResult.items.length} 条结果：</p>
              {searchResult.items.map((item, i) => (
                <button
                  key={i}
                  onClick={() => { setSelectedItem(item); setIsNew(false); }}
                  className={`block w-full rounded-lg border p-3 text-left hover:border-blue-500 ${
                    selectedItem === item ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <span className="font-medium">{item.customer.name}</span>
                  <span className="ml-2 text-gray-500">{item.customer.phone ?? ''}</span>
                  {item.vehicle && (
                    <span className="ml-2 text-gray-700">{item.vehicle.plate}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Selected existing customer/vehicle */}
          {selectedItem && (
            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{selectedItem.customer.name}</p>
                  <p className="text-sm text-gray-600">{selectedItem.customer.phone ?? '无手机号'}</p>
                  {selectedItem.vehicle && (
                    <p className="mt-1 text-sm text-gray-700">
                      {selectedItem.vehicle.plate}
                      {selectedItem.vehicle.brand && ` · ${selectedItem.vehicle.brand}`}
                      {selectedItem.vehicle.model && ` ${selectedItem.vehicle.model}`}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => { setSelectedItem(null); setIsNew(false); }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  重选
                </button>
              </div>
            </div>
          )}

          {/* New customer/vehicle form */}
          {isNew && !selectedItem?.vehicle && (
            <div className="mt-4 space-y-3">
              {!selectedItem && (
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="客户姓名"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                  <Input
                    placeholder="手机号"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                  />
                </div>
              )}
              <Input
                placeholder="车牌号 *"
                value={vehiclePlate}
                onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
              />
              <div className="grid grid-cols-3 gap-3">
                <Input
                  placeholder="品牌"
                  value={vehicleBrand}
                  onChange={(e) => setVehicleBrand(e.target.value)}
                />
                <Input
                  placeholder="车型"
                  value={vehicleModel}
                  onChange={(e) => setVehicleModel(e.target.value)}
                />
                <Input
                  placeholder="年份"
                  type="number"
                  value={vehicleYear}
                  onChange={(e) => setVehicleYear(e.target.value)}
                />
              </div>
              <Input
                placeholder="VIN（可选）"
                value={vehicleVin}
                onChange={(e) => setVehicleVin(e.target.value.toUpperCase())}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Repair items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>维修项目 ({items.length})</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleAddItem()}>
                + 手动添加
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Quick service item templates */}
          {serviceItems && serviceItems.length > 0 && items.length === 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {serviceItems.filter((s) => s.isQuick).slice(0, 8).map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleAddItem(item)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:border-blue-500 hover:bg-blue-50"
                >
                  {item.name} ¥{item.defaultPrice}
                </button>
              ))}
            </div>
          )}

          {items.length === 0 ? (
            <p className="text-gray-500">请添加维修项目</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-600">
                  <th className="pb-3 pr-4 font-medium">项目名称</th>
                  <th className="pb-3 pr-4 font-medium">数量</th>
                  <th className="pb-3 pr-4 font-medium">单价</th>
                  <th className="pb-3 pr-4 font-medium">金额</th>
                  <th className="pb-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-2 pr-4">
                      <input
                        className="w-full rounded border border-gray-200 px-2 py-1"
                        value={item.name}
                        onChange={(e) => handleItemChange(i, 'name', e.target.value)}
                        placeholder="项目名称"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        className="w-20 rounded border border-gray-200 px-2 py-1"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(i, 'quantity', e.target.value)}
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        className="w-24 rounded border border-gray-200 px-2 py-1"
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(i, 'unitPrice', e.target.value)}
                      />
                    </td>
                    <td className="py-2 pr-4 font-medium text-gray-900">¥{item.amount}</td>
                    <td className="py-2">
                      <button
                        onClick={() => handleRemoveItem(i)}
                        className="text-red-500 hover:text-red-700"
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200">
                  <td colSpan={3} className="pt-3 text-right text-gray-600">合计</td>
                  <td className="pt-3 pr-4 text-lg font-bold text-gray-900">¥{totalAmount}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Remark */}
      <Card>
        <CardHeader>
          <CardTitle>备注</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            className="w-full rounded-md border border-gray-300 p-3 text-sm"
            rows={3}
            placeholder="工单备注（可选）"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={() => navigate('/shop-admin/orders')}>
          取消
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? '创建中...' : '创建工单'}
        </Button>
      </div>
    </div>
  );
}
